
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import { API_CONFIG } from '@/config/apiConfig';
import { Loader2 } from 'lucide-react';

const ApiDebugger: React.FC = () => {
  const [storageData, setStorageData] = useState<Record<string, string>>({});
  const [isVisible, setIsVisible] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    if (isVisible) {
      const data: Record<string, string> = {};
      
      // Recolher dados do localStorage (exceto senhas)
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i) || '';
        if (key && !key.toLowerCase().includes('password') && !key.toLowerCase().includes('senha')) {
          try {
            let value = localStorage.getItem(key) || '';
            
            // Tentar desencriptar tokens
            if (key === 'api_auth_token' || key === 'HeaderToken' || key === 'api_key') {
              try {
                const decoded = atob(value);
                value = decoded.substring(0, 10) + '...';
              } catch (e) {
                value = value.substring(0, 10) + '...';
              }
            }
            
            data[key] = value;
          } catch (error) {
            data[key] = 'ERRO AO LER';
          }
        }
      }
      
      setStorageData(data);
    }
  }, [isVisible]);

  const testApiConnection = async () => {
    setIsTesting(true);
    const apiUrl = API_CONFIG.baseURL;
    if (!apiUrl) {
      toast({
        title: 'URL da API não configurada',
        description: 'Configure a URL da API nas configurações',
        variant: 'destructive',
      });
      setIsTesting(false);
      return;
    }

    toast({
      title: 'Testando conexão',
      description: 'Verificando conexão com a API...',
    });

    try {
      // Use a specific endpoint instead of the base URL
      const testEndpoint = `${apiUrl}/api/FormaPagamentoes`;
      
      const response = await fetch(testEndpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          ...(API_CONFIG.apiKey && { 'HeaderToken': API_CONFIG.apiKey })
        }
      });
      
      if (response.ok) {
        // First check the content type
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          try {
            await response.json(); // Try to parse as JSON to ensure it's valid
            toast({
              title: 'Conexão estabelecida',
              description: 'API respondeu corretamente com dados JSON',
            });
          } catch (jsonError) {
            toast({
              title: 'Resposta recebida, mas formato inválido',
              description: 'A resposta não está em formato JSON válido',
              variant: 'destructive',
            });
          }
        } else {
          // Handle HTML or other non-JSON responses
          const text = await response.text();
          if (text.includes('<!DOCTYPE') || text.includes('<html')) {
            toast({
              title: 'Recebeu página HTML ao invés de JSON',
              description: 'A API pode estar retornando uma página de login ou erro. Verifique a URL e autenticação.',
              variant: 'destructive',
            });
          } else {
            toast({
              title: 'Resposta recebida em formato não-JSON',
              description: `Tipo de conteúdo: ${contentType || 'desconhecido'}`,
              variant: 'destructive',
            });
          }
        }
      } else {
        toast({
          title: 'Erro na resposta da API',
          description: `Status: ${response.status} - ${response.statusText}`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Erro ao testar conexão:', error);
      toast({
        title: 'Erro de conexão',
        description: error instanceof Error ? error.message : 'Não foi possível conectar ao servidor',
        variant: 'destructive',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const clearToken = () => {
    localStorage.removeItem('api_auth_token');
    localStorage.removeItem('HeaderToken');
    localStorage.removeItem('api_key');
    
    toast({
      title: 'Tokens removidos',
      description: 'Os tokens de autenticação foram removidos',
    });
    
    setStorageData({
      ...storageData, 
      api_auth_token: 'REMOVIDO', 
      HeaderToken: 'REMOVIDO',
      api_key: 'REMOVIDO'
    });
  };

  if (!isVisible) {
    return (
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 bg-gray-100 opacity-70 hover:opacity-100"
      >
        Debug
      </Button>
    );
  }

  return (
    <Card className="fixed bottom-4 right-4 w-80 z-50 shadow-lg max-h-96 overflow-auto">
      <CardHeader className="py-3">
        <div className="flex justify-between items-center">
          <CardTitle className="text-sm">Depurador de API</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setIsVisible(false)}>X</Button>
        </div>
        <CardDescription className="text-xs">Informações de depuração para API</CardDescription>
      </CardHeader>
      <CardContent className="py-2">
        <h3 className="text-xs font-semibold mb-2">LocalStorage:</h3>
        <div className="space-y-1 text-xs">
          {Object.entries(storageData).map(([key, value]) => (
            <div key={key} className="flex justify-between">
              <Badge variant="outline" className="font-mono">{key}</Badge>
              <span className="text-gray-600 truncate max-w-[150px]">{value}</span>
            </div>
          ))}
        </div>
        
        <Separator className="my-3" />
        
        <h3 className="text-xs font-semibold mb-2">API Config:</h3>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <span>URL:</span>
            <span className="text-gray-600 truncate max-w-[180px]">{API_CONFIG.baseURL || 'Não configurado'}</span>
          </div>
          <div className="flex justify-between">
            <span>Token:</span>
            <span className="text-gray-600">{API_CONFIG.apiKey ? '✓ Configurado' : '✗ Não configurado'}</span>
          </div>
        </div>
        
        <Separator className="my-3" />
        
        <h3 className="text-xs font-semibold mb-2">Informações do ambiente:</h3>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <span>Hostname:</span>
            <span className="text-gray-600">{window.location.hostname}</span>
          </div>
          <div className="flex justify-between">
            <span>Pathname:</span>
            <span className="text-gray-600">{window.location.pathname}</span>
          </div>
          <div className="flex justify-between">
            <span>isProduction:</span>
            <span className="text-gray-600">
              {window.location.hostname.includes('primeconveniencia.com.br') || 
               window.location.hostname.includes('rspcloud.com.br') ? 'true' : 'false'}
            </span>
          </div>
        </div>
      </CardContent>
      <CardFooter className="py-2 flex space-x-2">
        <Button 
          size="sm" 
          className="w-full" 
          onClick={testApiConnection}
          disabled={isTesting}
        >
          {isTesting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Testar API
        </Button>
        <Button size="sm" variant="destructive" onClick={clearToken}>Limpar Token</Button>
      </CardFooter>
    </Card>
  );
};

export default ApiDebugger;
